// src/pages/admin/TaxSettings.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  Card,
  InputAdornment,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from '@mui/material';
import { 
  Save, 
  Refresh,
  Info,
  AttachMoney,
  Visibility,
  Edit,
  List as ListIcon,
  Search,
  ExpandMore,
  Percent,
  Close,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useGetProductTaxQuery,
  useSetProductTaxMutation,
  useListAllProductTaxesQuery,
} from '../../features/admin/globalSettingsApi.ts';

const TaxSettings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // UAT: VAT can be changed only by a Super Admin. Normalize the role values
  // saved by the existing login flow (Super Admin / super_admin / admin).
  const storedRole = String(localStorage.getItem('admin_type') || localStorage.getItem('role') || localStorage.getItem('user_role') || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
  const isSuperAdmin = storedRole === 'superadmin' || storedRole === 'superadministrator';

  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Tax states
  const [taxName, setTaxName] = useState('VAT');
  const [taxPercentage, setTaxPercentage] = useState('5');
  const [currency, setCurrency] = useState('AED');
  
  // Product tax list states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [productSearchStr, setProductSearchStr] = useState('');

  // View dialog states
  const [viewProduct, setViewProduct] = useState<any>(null);

  // Edit dialog states
  const [editProduct, setEditProduct]   = useState<any>(null);
  const [editTaxName, setEditTaxName]   = useState('');
  const [editTaxPer, setEditTaxPer]     = useState('');
  const [editCurrency, setEditCurrency] = useState('');
  const [isSavingRow, setIsSavingRow]   = useState(false);
  
  // Common states
  const [hasTaxChanges, setHasTaxChanges] = useState(false);
  
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' 
  });

  // API hooks
  const { 
    data: taxData, 
    isLoading: isTaxLoading, 
    error: taxError, 
    isError: isTaxError, 
    refetch: refetchTax 
  } = useGetProductTaxQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const [setProductTax, { isLoading: isTaxSaving }] = useSetProductTaxMutation();

  // Product tax list hook
  const { 
    data: productTaxListData, 
    isLoading: isListLoading, 
    error: listError, 
    isError: isListError, 
    refetch: refetchList 
  } = useListAllProductTaxesQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  // Load initial tax data
  useEffect(() => {
    console.log('=== taxData received ===', taxData);
    
    if (taxData && taxData.success) {
      if (taxData.tax_name !== undefined && taxData.tax_name !== null) {
        setTaxName(String(taxData.tax_name));
      } else if (taxData.tax1_name !== undefined && taxData.tax1_name !== null) {
        setTaxName(String(taxData.tax1_name));
      } else {
        setTaxName('VAT');
      }
      
      if (taxData.tax_per !== undefined && taxData.tax_per !== null) {
        setTaxPercentage(String(taxData.tax_per));
      } else if (taxData.tax1_per !== undefined && taxData.tax1_per !== null) {
        setTaxPercentage(String(taxData.tax1_per));
      } else {
        setTaxPercentage('5');
      }
      
      if (taxData.currency !== undefined && taxData.currency !== null) {
        setCurrency(String(taxData.currency));
      }
      
      setHasTaxChanges(false);
    }
  }, [taxData]);

  // Update hasTaxChanges when any tax field changes
  useEffect(() => {
    if (taxData && taxData.success) {
      const originalName = taxData.tax_name || taxData.tax1_name || 'VAT';
      const originalPercentage = taxData.tax_per || taxData.tax1_per || '';
      
      setHasTaxChanges(
        originalName !== taxName ||
        String(originalPercentage) !== taxPercentage ||
        (taxData.currency || 'AED') !== currency
      );
    }
  }, [taxName, taxPercentage, currency, taxData]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Tax handlers
  const handleTaxNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTaxName(event.target.value);
  };

  const handleTaxPercentageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === '' || /^\d+(\.\d{1,2})?$/.test(value)) {
      setTaxPercentage(value);
    }
  };

  const handleCurrencyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrency(event.target.value);
  };

  const handleResetTax = () => {
    if (taxData && taxData.success) {
      setTaxName(String(taxData.tax_name || taxData.tax1_name || 'VAT'));
      setTaxPercentage(String(taxData.tax_per || taxData.tax1_per || ''));
      setCurrency(String(taxData.currency || 'AED'));
      setHasTaxChanges(false);
    }
  };

  const handleSaveTax = async () => {
    if (!isSuperAdmin) {
      setSnackbar({ open: true, message: 'Only Super Admin can modify VAT settings.', severity: 'error' });
      return;
    }
    if (!window.confirm(`Confirm VAT change to ${taxPercentage}% ${taxName || 'VAT'}? This will update the configured product VAT value.`)) {
      return;
    }
    if (!taxName.trim()) {
      setSnackbar({ open: true, message: 'Please enter a tax name', severity: 'error' });
      return;
    }
    if (!taxPercentage || parseFloat(taxPercentage) < 0) {
      setSnackbar({ open: true, message: 'Please enter a valid tax percentage', severity: 'error' });
      return;
    }

    try {
      const result = await setProductTax({ tax_name: taxName, tax_per: taxPercentage, currency }).unwrap();
      if (result.success) {
        setSnackbar({ open: true, message: result.message || 'Tax settings updated successfully!', severity: 'success' });
        setHasTaxChanges(false);
        await Promise.all([refetchTax(), refetchList()]);
      } else {
        throw new Error(result.message || 'Failed to update tax settings');
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || 'Error updating tax settings.', severity: 'error' });
    }
  };

  // Product tax list handlers
  const handleChangePage = (event: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = async () => {
    try {
      await Promise.all([refetchTax(), refetchList()]);
      setSnackbar({ open: true, message: 'Tax settings refreshed successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to refresh tax settings', severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // ── View handlers ──────────────────────────────────────────────────────────
  const handleViewOpen  = (product: any) => setViewProduct(product);
  const handleViewClose = () => setViewProduct(null);

  // ── Edit handlers ──────────────────────────────────────────────────────────
  const handleEditOpen = (product: any) => {
    setEditProduct(product);
    setEditTaxName(product.tax1_name || product.tax_name || '');
    setEditTaxPer(String(product.tax1_per ?? product.tax_per ?? ''));
    setEditCurrency(product.currency || 'AED');
  };
  const handleEditClose = () => setEditProduct(null);

  const handleSaveRowTax = async () => {
    if (!editTaxName.trim()) {
      setSnackbar({ open: true, message: 'Tax name required', severity: 'error' }); return;
    }
    if (!editTaxPer || parseFloat(editTaxPer) < 0) {
      setSnackbar({ open: true, message: 'Valid tax % required', severity: 'error' }); return;
    }
    setIsSavingRow(true);
    try {
      const result = await setProductTax({
        product_id: editProduct.product_id,
        tax_name: editTaxName,
        tax_per: editTaxPer,
        currency: editCurrency,
      }).unwrap();
      if (result.success) {
        setSnackbar({ open: true, message: `Tax updated for product #${editProduct.product_id}!`, severity: 'success' });
        handleEditClose();
        await refetchList();
      } else {
        throw new Error(result.message);
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || 'Failed to update', severity: 'error' });
    } finally {
      setIsSavingRow(false);
    }
  };

  const isLoading = isTaxLoading || isListLoading;
  const isError = isTaxError || isListError;

  // Prepare product tax list data for table with search filter
  const productTaxList = productTaxListData?.product_tax_list || [];
  
  const filteredProductList = productTaxList.filter(product => {
    if (!productSearchStr) return true;
    const searchTerm = productSearchStr.toLowerCase();
    return (
      product.proj_name?.toLowerCase().includes(searchTerm) ||
      product.prod_name?.toLowerCase().includes(searchTerm) ||
      product.product_id?.toString().includes(searchTerm) ||
      product.tax1_name?.toLowerCase().includes(searchTerm) ||
      product.tax_name?.toLowerCase().includes(searchTerm)
    );
  });

  const paginatedList = filteredProductList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
            <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Percent sx={{ color: '#008060', fontSize: 28 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    Tax Settings
                  </Typography>
                </Box>
                
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleRefresh}
                  disabled={isLoading}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#c9cccf',
                    color: '#202223',
                    borderRadius: '8px',
                    fontWeight: 500,
                    '&:hover': { borderColor: '#202223', bgcolor: 'transparent' }
                  }}
                >
                  Refresh
                </Button>
              </Box>

              <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                {isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, bgcolor: 'white' }}>
                    <CircularProgress size={32} sx={{ color: '#008060' }} />
                  </Box>
                )}

                {isError && !isLoading && (
                  <Box sx={{ p: 3, bgcolor: 'white' }}>
                    <Alert severity="error">Failed to load tax settings. Please try again.</Alert>
                  </Box>
                )}

                {!isLoading && !isError && (
                  <Box sx={{ p: 3, bgcolor: 'white' }}>
                    {/* ── Tax Settings Section ── */}
                    <Accordion 
                      defaultExpanded 
                      sx={{ 
                        boxShadow: 'none', 
                        border: '1px solid #e1e3e5',
                        '&:before': { display: 'none' },
                        borderRadius: '12px !important',
                        overflow: 'hidden',
                        mb: 3
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{
                          bgcolor: '#f6f6f7',
                          borderBottom: '1px solid #e1e3e5',
                          '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 }
                        }}
                      >
                        <AttachMoney sx={{ color: '#008060', fontSize: 24 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>
                          Product Tax Settings
                        </Typography>
                        {hasTaxChanges && (
                          <Chip 
                            label="Unsaved Changes" 
                            size="small" 
                            sx={{ ml: 2, bgcolor: '#ff9800', color: 'white', fontWeight: 600 }}
                          />
                        )}
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          Set the default tax name and percentage that will be applied to all products. 
                          Individual products can override these settings.
                        </Typography>

                        {taxData && taxData.success && (
                          <Box sx={{ bgcolor: '#f6f6f7', p: 2, borderRadius: '8px', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Info sx={{ color: '#2196f3', fontSize: 20 }} />
                            <Typography variant="body2" color="text.secondary">
                              Current tax: <strong>
                                {taxData.tax_name || taxData.tax1_name || 'VAT'} 
                                {(taxData.tax_per || taxData.tax1_per) ? ` (${taxData.tax_per || taxData.tax1_per}%)` : ' (Not set)'}
                              </strong>
                              {taxData.currency && ` in ${taxData.currency}`}
                            </Typography>
                          </Box>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <Grid container spacing={3}>
                          <Grid item xs={12} md={4}>
                            <TextField
                              label="Tax Name" disabled={!isSuperAdmin}
                              value={taxName}
                              onChange={handleTaxNameChange}
                              fullWidth
                              placeholder="e.g., VAT, GST, Sales Tax"
                              helperText="Enter the name of the tax"
                              disabled={isTaxLoading}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              label="Tax Percentage" disabled={!isSuperAdmin}
                              value={taxPercentage}
                              onChange={handleTaxPercentageChange}
                              fullWidth
                              type="text"
                              placeholder="e.g., 5, 18, 20"
                              helperText="Enter the tax percentage"
                              disabled={isTaxLoading}
                              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              label="Currency" disabled={!isSuperAdmin}
                              value={currency}
                              onChange={handleCurrencyChange}
                              select
                              fullWidth
                              helperText="Select currency"
                              disabled={isTaxLoading}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                              SelectProps={{ native: true }}
                            >
                              <option value="AED">AED (د.إ)</option>
                              <option value="USD">USD ($)</option>
                              <option value="EUR">EUR (€)</option>
                              <option value="GBP">GBP (£)</option>
                              <option value="INR">INR (₹)</option>
                            </TextField>
                          </Grid>
                        </Grid>

                        <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
                          <Button
                            onClick={handleResetTax}
                            disabled={!hasTaxChanges || isTaxSaving}
                            sx={{ textTransform: 'none', fontWeight: 600, color: '#202223', borderRadius: '8px', px: 2.5 }}
                          >
                            Reset
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={isTaxSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                            onClick={handleSaveTax}
                            disabled={!hasTaxChanges || isTaxSaving || !taxName || !taxPercentage}
                            sx={{
                              bgcolor: '#008060', textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2.5,
                              '&:hover': { bgcolor: '#006e52' },
                              '&.Mui-disabled': { bgcolor: '#c9cccf', color: '#8c9196' }
                            }}
                          >
                            {isTaxSaving ? 'Saving...' : 'Save Tax Settings'}
                          </Button>
                        </Box>
                      </AccordionDetails>
                    </Accordion>

                    {/* ── Product Tax List Section ── */}
                    <Accordion 
                      defaultExpanded
                      sx={{ 
                        boxShadow: 'none', 
                        border: '1px solid #e1e3e5',
                        '&:before': { display: 'none' },
                        borderRadius: '12px !important',
                        overflow: 'hidden'
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{
                          bgcolor: '#f6f6f7',
                          borderBottom: '1px solid #e1e3e5',
                          '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 }
                        }}
                      >
                        <ListIcon sx={{ color: '#008060', fontSize: 24 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>
                          Product Tax List
                        </Typography>
                        {productTaxListData && productTaxListData.success && (
                          <Chip 
                            label={`${filteredProductList.length} products`}
                            size="small"
                            sx={{ ml: 2, bgcolor: '#e3f2fd', color: '#1976d2' }}
                          />
                        )}
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          View all products and their individual tax settings.
                        </Typography>

                        {/* Search input */}
                        <Box sx={{ mb: 3 }}>
                          <TextField
                            size="small"
                            placeholder="Search products by name, ID or tax name..."
                            value={productSearchStr}
                            onChange={(e) => {
                              setProductSearchStr(e.target.value);
                              setPage(0);
                            }}
                            fullWidth
                            sx={{ maxWidth: 400, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Search fontSize="small" sx={{ color: '#6d7175' }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>

                        {productTaxList.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 8, bgcolor: '#fafafa', borderRadius: '8px', border: '1px dashed #e1e3e5' }}>
                            <Info sx={{ fontSize: 48, color: '#bdbdbd', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom>No Product Tax Data</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Product tax information will appear here once available.
                            </Typography>
                          </Box>
                        ) : filteredProductList.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#fafafa', borderRadius: '8px' }}>
                            <Typography variant="body1" color="text.secondary">
                              No products found matching "{productSearchStr}"
                            </Typography>
                            <Button 
                              variant="text" 
                              onClick={() => setProductSearchStr('')}
                              sx={{ mt: 1, textTransform: 'none' }}
                            >
                              Clear Search
                            </Button>
                          </Box>
                        ) : (
                          <>
                            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e1e3e5' }}>
                              <Table>
                                <TableHead sx={{ bgcolor: '#f6f6f7' }}>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Product ID</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Product Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Tax Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Tax %</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Currency</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {paginatedList.map((product) => (
                                    <TableRow key={product.product_id} hover>
                                      <TableCell>{product.product_id}</TableCell>
                                      <TableCell>
                                        <Typography variant="body2" fontWeight={500}>
                                          {product.proj_name || product.prod_name || '-'}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={product.tax1_name || product.tax_name || '-'}
                                          size="small"
                                          sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 500 }}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2" fontWeight={500}>
                                          {product.tax1_per ?? product.tax_per ?? '-'}%
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Chip label={product.currency || '-'} size="small" variant="outlined" />
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                          <Tooltip title="View Details">
                                            <IconButton size="small" onClick={() => handleViewOpen(product)}>
                                              <Visibility fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Edit Tax">
                                            <IconButton size="small" color="primary" onClick={() => handleEditOpen(product)}>
                                              <Edit fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            
                            <TablePagination
                              rowsPerPageOptions={[5, 10, 25]}
                              component="div"
                              count={filteredProductList.length}
                              rowsPerPage={rowsPerPage}
                              page={page}
                              onPageChange={handleChangePage}
                              onRowsPerPageChange={handleChangeRowsPerPage}
                              sx={{ mt: 2 }}
                            />
                          </>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ══ View Dialog ══ */}
      <Dialog
        open={Boolean(viewProduct)}
        onClose={handleViewClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>
              Product Tax Details
            </Typography>
            <IconButton size="small" onClick={handleViewClose}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewProduct && (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Box sx={{ p: 2, bgcolor: '#f6f6f7', borderRadius: '8px' }}>
                <Typography variant="caption" color="text.secondary">Product ID</Typography>
                <Typography variant="body1" fontWeight={600}>#{viewProduct.product_id}</Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: '#f6f6f7', borderRadius: '8px' }}>
                <Typography variant="caption" color="text.secondary">Product Name</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {viewProduct.proj_name || viewProduct.prod_name || '-'}
                </Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: '#f6f6f7', borderRadius: '8px' }}>
                <Typography variant="caption" color="text.secondary">Tax Name</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {viewProduct.tax1_name || viewProduct.tax_name || '-'}
                </Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: '#f6f6f7', borderRadius: '8px' }}>
                <Typography variant="caption" color="text.secondary">Tax Percentage</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {viewProduct.tax1_per ?? viewProduct.tax_per ?? '-'}%
                </Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: '#f6f6f7', borderRadius: '8px' }}>
                <Typography variant="caption" color="text.secondary">Currency</Typography>
                <Typography variant="body1" fontWeight={600}>{viewProduct.currency || '-'}</Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleViewClose}
            variant="outlined"
            sx={{
              textTransform: 'none', fontWeight: 600,
              borderColor: '#c9cccf', color: '#202223', borderRadius: '8px',
              '&:hover': { borderColor: '#202223', bgcolor: '#f1f2f4' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ Edit Dialog ══ */}
      <Dialog
        open={Boolean(editProduct)}
        onClose={handleEditClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>
              Edit Tax — #{editProduct?.product_id}
            </Typography>
            <IconButton size="small" onClick={handleEditClose} disabled={isSavingRow}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {editProduct && (
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              {/* Read-only product name */}
              <Box sx={{ p: 2, bgcolor: '#f6f6f7', borderRadius: '8px' }}>
                <Typography variant="caption" color="text.secondary">Product</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {editProduct.proj_name || editProduct.prod_name || '-'}
                </Typography>
              </Box>

              <TextField
                label="Tax Name" disabled={!isSuperAdmin}
                value={editTaxName}
                fullWidth
                size="small"
                placeholder="e.g., VAT, GST"
                disabled={isSavingRow}
                onChange={e => setEditTaxName(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />

              <TextField
                label="Tax Percentage" disabled={!isSuperAdmin}
                value={editTaxPer}
                fullWidth
                size="small"
                type="text"
                placeholder="e.g., 5"
                disabled={isSavingRow}
                onChange={e => {
                  const v = e.target.value;
                  if (v === '' || /^\d+(\.\d{1,2})?$/.test(v)) setEditTaxPer(v);
                }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />

              <TextField
                label="Currency" disabled={!isSuperAdmin}
                value={editCurrency}
                fullWidth
                size="small"
                select
                disabled={isSavingRow}
                onChange={e => setEditCurrency(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                SelectProps={{ native: true }}
              >
                <option value="AED">AED (د.إ)</option>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
                <option value="INR">INR (₹)</option>
              </TextField>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleEditClose}
            disabled={isSavingRow}
            variant="outlined"
            sx={{
              textTransform: 'none', fontWeight: 600,
              bgcolor: '#f44336', color: '#eeeeee', borderRadius: '8px',
              '&:hover': { Color: '#eeeeee', bgcolor: '#f44336' }
            }}
          >
            Close
          </Button>
          <Button
            onClick={handleEditClose}
            disabled={isSavingRow}
            variant="outlined"
            sx={{
              textTransform: 'none', fontWeight: 600,
              bgcolor: '#F78E12', color: '#eeeeee', borderRadius: '8px',
              '&:hover': { Color: '#eeeeee', bgcolor: '#F78E12' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveRowTax}
            variant="contained"
            disabled={isSavingRow || !editTaxName || !editTaxPer}
            startIcon={isSavingRow ? <CircularProgress size={16} color="inherit" /> : <Save />}
            sx={{
              bgcolor: '#008060', textTransform: 'none', fontWeight: 600, borderRadius: '8px',
              '&:hover': { bgcolor: '#006e52' },
              '&.Mui-disabled': { bgcolor: '#c9cccf', color: '#8c9196' }
            }}
          >
            {isSavingRow ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TaxSettings;